import { useEffect, useMemo, useState } from 'react';
import { useCachedBlobUrl } from '../../Services/useBlobDownloader';
import {
  ArchiveTooLargeError,
  buildArchiveName,
  buildAttachmentsArchive,
  collectMessageAttachments,
  mapWithConcurrency,
  saveBlob,
} from '../../Services/attachmentsArchive';
import { useAlert } from '../AlertContext';

export const ARCHIVE_MIN_ATTACHMENTS = 2;
const FETCH_CONCURRENCY = 4;

export const hasArchivableAttachments = ({ images, files } = {}) =>
  collectMessageAttachments({ images, files }).length >= ARCHIVE_MIN_ATTACHMENTS;

const MessageArchiveDownload = ({ images, files }) => {
  const { getCachedBlobUrl } = useCachedBlobUrl();
  const { showAlert } = useAlert();
  const [isReady, setIsReady] = useState(false);
  const [isZipping, setIsZipping] = useState(false);

  const collected = useMemo(() => collectMessageAttachments({ images, files }), [images, files]);
  const itemsKey = collected.map((item) => `${item.kind}:${item.key || item.url}`).join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = useMemo(() => collected, [itemsKey]);

  useEffect(() => {
    if (items.length < ARCHIVE_MIN_ATTACHMENTS) return undefined;

    let alive = true;
    const ac = new AbortController();
    setIsReady(false);

    mapWithConcurrency(items, FETCH_CONCURRENCY, (item) =>
      item.key ? getCachedBlobUrl(item.key, item.kind, { signal: ac.signal }) : null,
    )
      .catch((err) => {
        if (err?.name !== 'AbortError') console.warn('Attachments preload failed', err);
      })
      .finally(() => {
        if (alive) setIsReady(true);
      });

    return () => {
      alive = false;
      ac.abort();
    };
  }, [items, getCachedBlobUrl]);

  const resolveHref = (item) => (item.key ? getCachedBlobUrl(item.key, item.kind) : item.url);

  const handleClick = async (e) => {
    e.stopPropagation();
    if (!isReady || isZipping) return;

    setIsZipping(true);
    try {
      const { blob, failed } = await buildAttachmentsArchive(items, resolveHref);
      saveBlob(blob, buildArchiveName());
      if (failed.length) {
        showAlert(
          'warning',
          `Archive created without ${failed.length} of ${items.length} attachments: ${failed.join(', ')}`,
        );
      }
    } catch (err) {
      if (err instanceof ArchiveTooLargeError) {
        showAlert('fail', `${err.message}. Please download files individually.`);
      } else {
        console.error('Failed to build attachments archive', err);
        showAlert('fail', 'Failed to create archive');
      }
    } finally {
      setIsZipping(false);
    }
  };

  if (items.length < ARCHIVE_MIN_ATTACHMENTS) return null;

  const isBusy = !isReady || isZipping;
  let label = `Download all ${items.length} attachments as ZIP`;
  if (!isReady) label = 'Loading…';
  else if (isZipping) label = 'Creating archive…';

  return (
    <button
      type="button"
      className={`file-download-link archive-download-link ${isBusy ? 'is-loading' : ''}`}
      onClick={handleClick}
      title={label}
      aria-label={label}
      aria-busy={isBusy ? 'true' : undefined}
      aria-disabled={isBusy ? 'true' : undefined}
    >
      {isBusy ? (
        <svg width="20" height="20" viewBox="0 0 50 50" aria-hidden="true">
          <circle
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray="31.4 188.4"
          >
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 25 25"
              to="360 25 25"
              dur="0.9s"
              repeatCount="indefinite"
            />
          </circle>
        </svg>
      ) : (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          <path d="M14 3v5h5" />
          <path d="M10 3.5v.5M12 5.5v.5M10 7.5v.5" />
          <path d="M12 10.5v7" />
          <path d="M9.5 15 12 17.5l2.5-2.5" />
        </svg>
      )}
    </button>
  );
};

export default MessageArchiveDownload;
